"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { TickIcon } from "icons";
import React, { Dispatch, SetStateAction } from "react";
import { useForm } from "react-hook-form";
import {
  Button,
  Form,
  Heading,
  Link,
  Modal,
  Text,
  TextField,
  TextButton,
} from "ui-components";

import { CookieKeys } from "@/enums/CookieKeys.enum";
import { useCookies } from "@/hooks/useCookies";
import useGetUserCountry from "@/hooks/useGetUserCountry";
import { useNavigate } from "@/hooks/useNavigate";
import {
  emailCollectionSchema,
  EmailCollectionSchemaType,
} from "@/src/app/[locale]/(public)/jobs/[id]/schema/email-collection";
import {
  LoginResponse,
  NonMemberEntity,
  useAppCreateEmailCollection,
} from "@/src/hooks/api/generated";
import { brevoEmailCollection } from "@/utils/brevoClient";

interface IEmailCollectionProps {
  jobSlug?: string;
  isShowModal: boolean;
  setIsShowModal: Dispatch<SetStateAction<boolean>>;
  handleApplyClick: () => void;
}

export default function EmailCollection({
  jobSlug,
  isShowModal,
  setIsShowModal,
  handleApplyClick,
}: IEmailCollectionProps): JSX.Element {
  const { push } = useNavigate();
  const { country: userCountry } = useGetUserCountry();
  const { setCookie } = useCookies();
  const { mutate: createEmailCollection, status } =
    useAppCreateEmailCollection();

  const {
    register,
    handleSubmit,
    getFieldState,
    setError,
    reset,
    clearErrors,
    formState: { errors },
  } = useForm<EmailCollectionSchemaType>({
    resolver: zodResolver(emailCollectionSchema),
  });

  const onSubmit = (data: EmailCollectionSchemaType): void => {
    const email = data.email?.trim();

    if (!email) {
      setError("email", {
        type: "manual",
        message: "Email is required to register.",
      });
      return;
    }

    createEmailCollection(
      {
        data: {
          email,
        },
      },
      {
        onSuccess: (successData: NonMemberEntity) => {
          handleEmailCollection(email, successData);
        },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        onError: (error: any) => {
          let errorMessage = "";
          if (error && typeof error === "object" && error.message) {
            errorMessage = error.message;
          }
          let existingUser;
          if (
            errorMessage ===
            "Email already associated with an account. Please log in to continue."
          ) {
            existingUser =
              error.user || error.response?.data?.user || error.response?.user;
          }
          handleEmailCollection(
            email,
            undefined,
            existingUser as LoginResponse
          );
        },
      }
    );
  };

  const handleEmailCollection = (
    email?: string,
    successData?: NonMemberEntity,
    existingUser?: LoginResponse
  ): void => {
    reset({
      email: "",
    });
    clearErrors();
    setIsShowModal(false);
    const maxAge = 2 * 30 * 24 * 60 * 60; // 2 months in seconds
    setCookie({
      key: CookieKeys.EmailCollect,
      value: `/jobs/${jobSlug}`,
      maxAge,
    });
    setCookie({
      key: CookieKeys.NonMemberUUID,
      value: successData?.uuid || "",
      maxAge,
    });
    setCookie({
      key: CookieKeys.Redirect,
      value: `/jobs/${jobSlug}`,
      maxAge: 2 * 60 * 60, // 2 Hour in seconds
    });

    if (email) {
      const isAuthenticatedContext = !!existingUser;
      const uuid = successData?.uuid || existingUser?.uuid || "";

      brevoEmailCollection(
        email,
        isAuthenticatedContext,
        uuid,
        existingUser,
        userCountry || ""
      );
    }
    handleApplyClick();
  };

  const handleModalClose = (): void => {
    setCookie({
      key: CookieKeys.EmailCollectClose,
      value: "true",
      maxAge: 30 * 60, // 30 minutes in seconds
    });
  };

  const handleSignUp = (): void => {
    setCookie({
      key: CookieKeys.Redirect,
      value: `/jobs/${jobSlug}`,
      maxAge: 2 * 60 * 60, // 2 Hour in seconds
    });
    push("/signup");
  };

  return (
    <div>
      <Modal
        animatedModalClassName="md:min-w-[520px] flex flex-1 items-center justify-center"
        isOpen={isShowModal}
        onOpenChange={(isOpen) => {
          setIsShowModal(isOpen);
          if (!isOpen) {
            handleModalClose();
          }
        }}
        isBackgroundBlur
        closeIcons={false}
      >
        <div className="flex flex-1 flex-col w-full md:px-10 px-4 max-h-[80vh] gap-4 overflow-y-auto">
          <Form
            onSubmit={handleSubmit(onSubmit)}
            className="flex flex-1 flex-col w-full max-h-[80vh] gap-2"
          >
            <Heading align="center">
              Find your next role with an inclusive employer
            </Heading>
            <Text align="center" className="md:w-5/6 mx-auto">
              Join the myGwork LGBTQ+ Business Community for free and get
              instant access to:
            </Text>
            <div className="flex items-center gap-x-2.5 mt-3">
              <TickIcon className="w-[14px]" />
              <Text>New job opportunities from equality-focused employers</Text>
            </div>
            <div className="flex items-center gap-x-2.5">
              <TickIcon className="w-[14px]" />
              <Text>Career tips, insights & industry updates</Text>
            </div>
            <div className="flex items-center gap-x-2.5 mb-3">
              <TickIcon className="w-[14px]" />
              <Text>Events that support your professional growth</Text>
            </div>

            <div className="flex flex-col md:flex-row gap-x-5">
              <TextField
                label="Enter your email to start your job search (optional):"
                placeholder="Email"
                register={register("email")}
                errorMessage={errors.email?.message}
                hasValue={getFieldState("email").isDirty}
              />
            </div>
            <Button
              isDisabled={status === "pending"}
              isLoading={status === "pending"}
              type="submit"
              className="mt-1 mx-auto !w-80"
            >
              Register and continue to application
            </Button>
            <TextButton
              onPress={() => handleEmailCollection()}
              className="mb-1 mx-auto !bg-white !hover:shadow-none"
            >
              <Text className="underline">
                Continue to application without registering
              </Text>
            </TextButton>
          </Form>
          <Text align="center">
            You can unsubscribe at any time. By providing us your email address,
            you agree to our{" "}
            <Link href="/terms-of-use" target="_blank">
              Terms of Use
            </Link>{" "}
            and{" "}
            <Link href="/privacy-policy" target="_blank">
              Privacy Policy
            </Link>
          </Text>
          <Text align="center">
            Not a member yet?{" "}
            <Button
              className="!bg-white !h-0 !w-0 !underline !inline-flex hover:!shadow-none"
              onClick={handleSignUp}
            >
              Sign up
            </Button>
          </Text>
          <Text align="center">
            Already a member?{" "}
            <Button
              className="!bg-white !h-0 !w-0 !underline !inline-flex hover:!shadow-none"
              onClick={() => push(`/signin?redirect-to=/jobs/${jobSlug}`)}
            >
              Log in
            </Button>
          </Text>
        </div>
      </Modal>
    </div>
  );
}
